import { useEffect, useRef, useState } from "react";
import { ArrowRight, Baby, Clock } from "lucide-react";
import { useDebugControls, HeroButtonStyle, HeadlineStyle } from "@/components/debug-controls-context";
import { useContactOverlay, type ContactCategory } from "@/components/contact-overlay-context";
import { cn } from "@/lib/utils";
import { defaultHeroContent, fetchHeroContent, type HeroContent } from "@/lib/services/hero.service";
import { HERO_BACKGROUND_IMAGE_URL, getHeroVideoMimeType, type HeroViewportMode, useHeroVideoUrl } from "@/lib/hero-media";
import { fetchOpeningHours, type OpeningHourItem } from "@/lib/services/opening-hours.service";
import { Skeleton } from "@/components/ui/skeleton";
import { InstagramSocialBar } from "@/components/instagram-social-bar";


type HeroSectionProps = {
  content?: Partial<HeroContent>;
  preview?: boolean;
  className?: string;
  viewportMode?: HeroViewportMode;
};

export function HeroSection({ content, preview = false, className, viewportMode = "auto" }: HeroSectionProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showVideo, setShowVideo] = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [remoteContent, setRemoteContent] = useState<HeroContent | null>(null);
  const [isLoading, setIsLoading] = useState(!content);
  const [kinderDrawerOpen, setKinderDrawerOpen] = useState(false);
  const [childcare, setChildcare] = useState<OpeningHourItem[]>([]);
  const [openingHours, setOpeningHours] = useState<OpeningHourItem[]>([]);
  const [childcareLoading, setChildcareLoading] = useState(true);
  const { debugEnabled, heroButtonStyle, headlineStyle } = useDebugControls();
  const { openContact } = useContactOverlay();
  const backgroundVideoUrl = useHeroVideoUrl(viewportMode);

  useEffect(() => {
    let isMounted = true;
    fetchOpeningHours().then((data) => {
      if (isMounted) {
        const active = data.filter(i => i.is_active);
        setChildcare(active.filter(i => i.section === 'childcare'));
        setOpeningHours(active.filter(i => i.section === 'opening_hours'));
        setChildcareLoading(false);
      }
    });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (content) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    fetchHeroContent().then((data) => {
      if (isMounted) {
        setRemoteContent(data);
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [content]);

  const effectiveContent: HeroContent = {
    ...defaultHeroContent,
    ...(remoteContent ?? {}),
    ...(content ?? {}),
  };

  const appliedHeadlineStyle = debugEnabled ? headlineStyle : effectiveContent.headline_style;
  const appliedButtonStyle = debugEnabled ? heroButtonStyle : effectiveContent.cta_style;

  useEffect(() => {
    if (!debugEnabled && !isLoading) {
      setShowVideo(effectiveContent.background_type === "video");
    }
  }, [effectiveContent.background_type, debugEnabled, isLoading]);

  useEffect(() => {
    setIsVideoReady(false);
    setVideoFailed(false);
  }, [backgroundVideoUrl, effectiveContent.background_type]);

  if (isLoading) {
    return (
      <section className={cn(
        "relative w-full flex flex-col justify-end section-hero overflow-hidden border-b border-white/10 z-0 font-kanit",
        preview ? "min-h-[70vh]" : "hero-viewport-height fixed top-0 left-0 right-0",
        className
      )}>
        <div className="absolute inset-0 bg-background/80" />
        <div className="w-full page-content relative z-10">
          <div className="flex flex-col items-start w-fit max-w-full gap-4">
             <Skeleton className="h-16 w-[300px] md:w-[500px]" />
             <Skeleton className="h-16 w-[200px] md:w-[400px]" />
             <div className="space-y-2 py-4">
                <Skeleton className="h-4 w-[250px] md:w-[400px]" />
                <Skeleton className="h-4 w-[200px] md:w-[350px]" />
             </div>
             <Skeleton className="h-12 w-40 rounded-full" />
          </div>
        </div>
      </section>
    );
  }
  
  const getButtonClasses = (style: HeroButtonStyle, isMobile: boolean = false) => {
    // Base classes - optimized mobile padding
    const padding = isMobile ? "px-5 py-2.5" : "px-6 md:px-8 py-3 md:py-4";
    const textSize = isMobile ? "text-sm font-bold" : "text-sm md:text-lg font-bold";

    let baseClasses = `rounded-full ${padding} ${textSize} tracking-wide transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer relative overflow-hidden whitespace-nowrap`;
    
    if (style !== "flat") {
        baseClasses += " uppercase";
    }

    switch (style) {
      case "flat":
        return `${baseClasses.replace("rounded-full", "rounded-lg")} bg-primary text-primary-foreground hover:bg-primary/90 shadow-none`;
      case "ghost":
        return `${baseClasses} bg-transparent border-2 border-primary text-white hover:bg-primary hover:text-primary-foreground hover:border-primary shadow-sm hover:shadow-md`;
      case "ghost-blue":
        return `${baseClasses} bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary shadow-[0_0_20px_rgba(11,118,132,0.2)] hover:shadow-[0_0_30px_rgba(11,118,132,0.4)]`;
      case "ghost-blue-black":
        return `${baseClasses} bg-black/40 backdrop-blur-sm border-2 border-primary text-white hover:bg-black/60 hover:border-primary`;
      case "glass":
        return `${baseClasses} bg-primary/20 border border-primary/50 text:white backdrop-blur-sm hover:bg-primary/40 shadow-[0_0_20px_rgba(11,118,132,0.2)] hover:shadow-[0_0_30px_rgba(11,118,132,0.4)]`;
      case "glass-white":
        return `${baseClasses} bg-white/10 border border-white/30 text-white backdrop-blur-md hover:bg-white/20 shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]`;
      case "minimal":
        return `${baseClasses} bg-white text-black hover:bg-gray-100 border border-transparent hover:border-white/20 shadow-lg hover:shadow-xl hover:scale-105`;
      case "soft":
        return `${baseClasses} bg-primary/80 text-white hover:bg-primary backdrop-blur-sm shadow-lg shadow-primary/10 hover:shadow-primary/30`;
      case "gradient-border":
        return `${baseClasses} bg-gradient-to-r from-primary to-accent text-white border border-white/20 hover:brightness-110 shadow-[0_0_15px_rgba(11,118,132,0.5)]`;
      case "shimmer":
        return `${baseClasses} bg-primary text-white overflow-hidden before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:transition-all before:duration-1000 hover:before:left-full hover:shadow-[0_0_30px_rgba(11,118,132,0.6)]`;
      case "white-blue":
        return `${baseClasses} bg-white text-primary hover:bg-gray-50 shadow-lg hover:shadow-xl hover:scale-105`;
      case "white-blue-border":
        return `${baseClasses} bg-white text-primary border-2 border-primary hover:bg-primary hover:text-white shadow-lg hover:shadow-primary/30`;
      case "neu-pill":
        return `${baseClasses} bg-primary/30 backdrop-blur-md border border-white/30 text-white shadow-lg hover:bg-primary/40 hover:shadow-xl hover:border-white/50`;
      case "turquoise-pill":
        return `${baseClasses} bg-[#a8d8db] text-black hover:bg-[#8dbdc2] shadow-lg hover:shadow-xl hover:scale-105 font-bold`;
      case "original":
        return `${baseClasses} bg-primary text-white hover:bg-primary/90 uppercase`;
      default:
        return `${baseClasses} bg-primary text-white hover:bg-primary/90 uppercase`;
    }
  };

  const getHeadlineClasses = (style: HeadlineStyle) => {
    // "wirklich nur outlines": zentrale CSS-Utilities, kein doppeltes [text-stroke] + [-webkit-text-stroke]
    const outlineWhiteClass = "text-outline-only text-outline-white";
    const outlineCleanWhiteClass = "text-clean-outline-white";
    const outlinePrimaryClass = "text-outline-only text-outline-primary";
    const outlineWeakWhiteClass = "text-outline-only text-outline-white-weak";
    switch (style) {
      case "solid-primary":
        return { h1: "text-white", span: "text-primary" };
      case "white":
        return { h1: "text-white", span: "text-white" };
      case "subtle-gray":
        return { h1: "text-white", span: "text-white/60" };
      case "outline-primary":
        return { h1: "text-white", span: outlinePrimaryClass };
      case "outline-white":
        return { h1: "text-white", span: outlineWhiteClass };
      case "outline-gradient":
        // Glow ist optional – Outline bleibt sauber via Utilities
        return { h1: "text-white", span: `${outlineWhiteClass} drop-shadow-[0_0_5px_rgba(11,118,132,1)]` };
      case "outline-glow-white":
        return { h1: "text-white", span: `${outlineWhiteClass} drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]` };
      case "transparent-gradient":
        return { h1: "text-white", span: outlineWeakWhiteClass };
      case "underline":
        return { h1: "text-white", span: "text-white underline decoration-primary decoration-4 underline-offset-8" };
      case "highlight":
        return { h1: "text-white", span: "text-white bg-primary/20 px-2 rounded" };
      case "all-outline-white":
        return { h1: outlineWhiteClass, span: outlineWhiteClass };
      case "split-outline-white":
        return { h1: outlineWhiteClass, span: "text-white" };
      case "split-white-outline":
        return { h1: "text-white", span: outlineCleanWhiteClass };
      case "gradient":
      default:
        return { h1: "text-white", span: "bg-gradient-to-r from-white to-primary bg-clip-text text-transparent" };
    }
  };

  const headlineClasses = getHeadlineClasses(appliedHeadlineStyle);
  const isOutline = appliedHeadlineStyle.includes('outline');

  const backgroundImageUrl = effectiveContent.background_image_url?.trim() || HERO_BACKGROUND_IMAGE_URL;
  const resolvedShowVideo = showVideo && backgroundVideoUrl.length > 0;
  const overlayStrength = Math.min(Math.max(effectiveContent.overlay_strength ?? 0.3, 0), 0.8);
  const videoBrightness = effectiveContent.video_brightness ?? 1.0;
  const videoSaturation = effectiveContent.video_saturation ?? 1.0;
  const videoBlur = effectiveContent.video_blur ?? 0;
  const videoTintColor = effectiveContent.video_tint_color ?? "#000000";
  const videoTintOpacity = effectiveContent.video_tint_opacity ?? 0;
  const videoFilterStyle = `brightness(${videoBrightness}) saturate(${videoSaturation})${videoBlur > 0 ? ` blur(${videoBlur}px)` : ""}`;
  const shouldRenderVideo = resolvedShowVideo && !videoFailed;
  const shouldShowFallbackImage = !resolvedShowVideo || videoFailed;

  // Vignette: inset box-shadow per Kante
  const vTop    = effectiveContent.vignette_top    ?? 0;
  const vBottom = effectiveContent.vignette_bottom ?? 0;
  const vLeft   = effectiveContent.vignette_left   ?? 0;
  const vRight  = effectiveContent.vignette_right  ?? 0;
  const vignetteSize = 220; // px – Weichzeichner-Breite der Kante
  const vignetteBoxShadow = [
    vTop    > 0 ? `inset 0 ${vignetteSize}px ${vignetteSize}px -${vignetteSize * 0.4}px rgba(0,0,0,${vTop.toFixed(2)})` : null,
    vBottom > 0 ? `inset 0 -${vignetteSize}px ${vignetteSize}px -${vignetteSize * 0.4}px rgba(0,0,0,${vBottom.toFixed(2)})` : null,
    vLeft   > 0 ? `inset ${vignetteSize}px 0 ${vignetteSize}px -${vignetteSize * 0.4}px rgba(0,0,0,${vLeft.toFixed(2)})` : null,
    vRight  > 0 ? `inset -${vignetteSize}px 0 ${vignetteSize}px -${vignetteSize * 0.4}px rgba(0,0,0,${vRight.toFixed(2)})` : null,
  ].filter(Boolean).join(", ");
  const hasVignette = vignetteBoxShadow.length > 0;
  const sublineParts = effectiveContent.subline.split(/\n|\\n/);
  const validContactCategories: ContactCategory[] = ["Allgemeines", "Probetraining", "Mitgliedschaft"];
  const resolveContactCategory = (value: string): ContactCategory =>
    validContactCategories.includes(value as ContactCategory) ? (value as ContactCategory) : "Allgemeines";

  const handleCtaClick = () => {
    if (preview) {
      return;
    }

    if (effectiveContent.cta_target_type === "external") {
      if (effectiveContent.cta_target_value) {
        window.open(effectiveContent.cta_target_value, "_blank", "noopener,noreferrer");
      }
      return;
    }

    openContact({ category: resolveContactCategory(effectiveContent.cta_target_value) });
  };

  return (
    <section 
      id="top" 
      ref={containerRef}
      className={cn(
        "relative w-full flex flex-col justify-end section-hero overflow-hidden border-b border-white/10 z-0 font-kanit",
        preview ? "min-h-[70vh]" : "hero-viewport-height fixed top-0 left-0 right-0",
        className
      )}
    >
      
      {/* Hero Background */}
      <div className="absolute inset-0 -z-10 bg-black">
        {shouldShowFallbackImage && (
          <img
            src={backgroundImageUrl}
            alt="Training im Studio"
            className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700"
          />
        )}
        {shouldRenderVideo ? (
          <video
            key={backgroundVideoUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            onLoadedData={() => setIsVideoReady(true)}
            onCanPlay={() => setIsVideoReady(true)}
            onPlaying={() => setIsVideoReady(true)}
            onError={() => {
              setVideoFailed(true);
              setIsVideoReady(false);
            }}
            onContextMenu={(e) => e.preventDefault()}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-700"
            style={{
              filter: videoFilterStyle,
              opacity: isVideoReady ? 1 : 0,
            }}
          >
            <source src={backgroundVideoUrl} type={getHeroVideoMimeType(backgroundVideoUrl)} />
          </video>
        ) : null}
        {/* Tint overlay – nur über dem Video, unter dem Gradient-Overlay */}
        {shouldRenderVideo && isVideoReady && videoTintOpacity > 0 && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundColor: videoTintColor, opacity: videoTintOpacity }}
          />
        )}
        {/* Vignette – weiche Randverdunklung per Kante */}
        {shouldRenderVideo && isVideoReady && hasVignette && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ boxShadow: vignetteBoxShadow }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" style={{ opacity: overlayStrength }} />
      </div>

      <div 
        className="w-full flex flex-col items-start text-left relative page-content"
      >
        
        {/* --- UNIFIED LAYOUT (Mobile & Desktop) --- */}
        <div className="flex flex-col items-start w-fit max-w-full">


            {/* Headline - Two Lines */}
            <h1 className={`text-[2.5rem] md:text-5xl lg:text-6xl font-black uppercase mb-4 md:mb-4 leading-[0.95] md:leading-[0.8] ${isOutline ? 'font-sans tracking-normal' : 'tracking-tight'}`}>
              <span className={headlineClasses.h1}>{effectiveContent.headline_line1}</span>
              <br />
              <span className={headlineClasses.span}>{effectiveContent.headline_line2}</span>
            </h1>
            
            {/* Stacked Layout: Subline then Button (Left aligned) */}
            <div className="flex flex-col items-start gap-4 md:gap-6 animate-in slide-in-from-bottom-4 duration-700 delay-200">
                {/* Subline (Left aligned, light gray) */}
                <p className="text-base md:text-xl lg:text-xl font-light leading-relaxed text-[#DDDDDD] max-w-2xl text-left">
                  {sublineParts.map((part, index) => (
                    <span key={`subline-${index}`}>
                      {part}
                      {index < sublineParts.length - 1 ? <br /> : null}
                    </span>
                  ))}
                </p>

                {/* CTA Button (Left aligned, with margin top) */}
                <div className="flex justify-start pt-1 md:pt-2">
                  <button
                    type="button"
                    onClick={handleCtaClick}
                    className="inline-block group w-auto"
                  >
                    <div className={getButtonClasses(appliedButtonStyle, true)}>
                      {effectiveContent.cta_label}
                      <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  </button>
                </div>
            </div>
        </div>


        {/* ── Öffnungszeiten / Kinderbetreuung ── */}
        {!preview && (
          <>
            {/* ── MOBILE: Side Tab (vertikal, rechter Rand) ── */}
            <div className="md:hidden">
              <div className="fixed right-2.5 top-[45%] -translate-y-1/2 z-[101] flex flex-row items-center gap-1">

                {/* Panel */}
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-500",
                    "rounded-2xl border border-primary/20",
                    kinderDrawerOpen
                      ? "w-[85vw] opacity-100 pointer-events-auto"
                      : "w-0 opacity-0 pointer-events-none"
                  )}
                  style={{
                    maxHeight: "min(calc(100dvh - 120px), 600px)",
                    background: "rgba(14,24,36,0.55)",
                    backdropFilter: "blur(28px) saturate(160%)",
                    WebkitBackdropFilter: "blur(28px) saturate(160%)",
                    border: "1px solid rgba(155,236,250,0.25)",
                    borderRadius: "16px",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 8px 32px rgba(0,0,0,0.5)",
                    transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                  }}
                >
                  <div className="overflow-y-auto" style={{ scrollbarWidth: "none", maxHeight: "inherit" }}>
                    <div 
                      className={cn(
                        "py-5 px-5 flex flex-col gap-4 w-[85vw] transition-opacity duration-300",
                        kinderDrawerOpen ? "opacity-100 delay-200" : "opacity-0"
                      )}
                    >
                      {/* Öffnungszeiten */}
                      <div>
                        <div className="flex itemsoch-center gap-2 mb-3">
                          <Clock className="w-4 h-4 text-primary" />
                          <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-primary">Öffnungszeiten</span>
                        </div>
                        {childcareLoading ? (
                          <div className="space-y-2"><Skeleton className="h-4 w-full rounded" /><Skeleton className="h-4 w-full rounded" /></div>
                        ) : openingHours.map((item) => {
                          const lines = item.time_range.split('\n').map(l => l.trim()).filter(l => l);
                          return (
                            <div key={item.id} className="flex justify-between items-baseline py-1.5 border-b border-white/[0.08] last:border-0">
                              <span className="text-white/70 text-[14px] font-medium shrink-0 mr-3">{item.day_label}</span>
                              <div className="flex flex-col items-end gap-0.5">
                                {lines.map((line, idx) => <span key={idx} className="text-white text-[14px] font-semibold tabular-nums">{line}</span>)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Divider */}
                      <div className="h-px bg-white/[0.08]" />
                      {/* Kinderbetreuung */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Baby className="w-4 h-4 text-primary" />
                          <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-primary">Kidsbetreuung</span>
                          <span className="ml-auto text-[10px] text-white/40 font-medium tracking-widest uppercase">kostenlos</span>
                        </div>
                        {childcareLoading ? (
                          <div className="space-y-2"><Skeleton className="h-4 w-full rounded" /><Skeleton className="h-4 w-full rounded" /></div>
                        ) : childcare.map((item) => {
                          const lines = item.time_range.split('\n').map(l => l.trim()).filter(l => l);
                          return (
                            <div key={item.id} className="flex justify-between items-baseline py-1.5 border-b border-white/[0.08] last:border-0">
                              <span className="text-white/70 text-[14px] font-medium shrink-0 mr-3">{item.day_label}</span>
                              <div className="flex flex-col items-end gap-0.5">
                                {lines.map((line, idx) => <span key={idx} className="text-white text-[14px] font-semibold tabular-nums">{line}</span>)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab-Button vertikal */}
                <button
                  type="button"
                  onClick={() => setKinderDrawerOpen(!kinderDrawerOpen)}
                  className={cn(
                    "flex flex-col items-center justify-center gap-2.5",
                    "w-10 py-5 shrink-0",
                    "rounded-xl border",
                    kinderDrawerOpen ? "border-primary" : "border-white/30",
                    "cursor-pointer transition-all duration-300",
                  )}
                  style={{
                    background: "rgba(14,24,36,0.50)",
                    backdropFilter: "blur(28px) saturate(160%)",
                    WebkitBackdropFilter: "blur(28px) saturate(160%)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                  }}
                >
                  <Clock className={cn("w-4 h-4 shrink-0 transition-colors duration-300", kinderDrawerOpen ? "text-primary" : "text-white/80")} />
                  <span
                    className={cn("text-[12px] font-bold uppercase tracking-[0.15em] transition-colors duration-300", kinderDrawerOpen ? "text-primary" : "text-white/90")}
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    Kinderbetreuung & Öffnungszeiten
                  </span>
                  <Baby className={cn("w-4 h-4 shrink-0 transition-colors duration-300", kinderDrawerOpen ? "text-primary" : "text-white/80")} />
                </button>
              </div>
            </div>

            {/* ── DESKTOP: Side Drawer (unverändert) ── */}
            <div
              className="hidden md:flex fixed right-2.5 top-1/2 -translate-y-1/2 z-[101] flex-row items-center gap-1.5"
            >
              {/* Content Panel */}
              <div
                className={cn(
                  "overflow-hidden transition-all duration-500",
                  "rounded-2xl border border-primary/30",
                  kinderDrawerOpen
                    ? "w-[19rem] opacity-100 pointer-events-auto"
                    : "w-0 opacity-0 pointer-events-none"
                )}
                style={{
                  maxHeight: "min(calc(100vh - 160px), 620px)",
                  background: "rgba(14,22,32,0.82)",
                  backdropFilter: "blur(32px) saturate(180%)",
                  WebkitBackdropFilter: "blur(32px) saturate(180%)",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.65)",
                  transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              >
                <div className="overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none", maxHeight: "inherit" }}>
                  <div 
                    className={cn(
                      "min-w-[19rem] py-6 transition-opacity duration-300",
                      kinderDrawerOpen ? "opacity-100 delay-200" : "opacity-0"
                    )}
                  >
                    <div className="px-5 pb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="text-primary text-[11px] font-bold uppercase tracking-[0.3em]">Öffnungszeiten</span>
                      </div>
                      {childcareLoading ? (
                        <div className="space-y-2"><Skeleton className="h-5 w-full rounded" /><Skeleton className="h-5 w-full rounded" /></div>
                      ) : openingHours.map((item) => {
                        const lines = item.time_range.split('\n').map(l => l.trim()).filter(l => l);
                        return (
                          <div key={item.id} className="flex justify-between items-baseline py-2.5 border-b border-white/[0.08] last:border-0">
                            <span className="text-white/75 text-[15px] font-medium whitespace-nowrap shrink-0 mr-4">{item.day_label}</span>
                            <div className="flex flex-col items-end gap-0.5">
                              {lines.map((line, idx) => <span key={idx} className="text-white text-[15px] font-semibold tabular-nums whitespace-nowrap">{line}</span>)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mx-5 my-3 h-px bg-white/[0.08]" />
                    <div className="px-5 pt-3 pb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Baby className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="text-primary text-[11px] font-bold uppercase tracking-[0.3em]">Kinderbetreuung</span>
                        <span className="ml-auto text-[10px] text-white/40 font-medium tracking-widest uppercase">kostenlos</span>
                      </div>
                      {childcareLoading ? (
                        <div className="space-y-2"><Skeleton className="h-5 w-full rounded" /><Skeleton className="h-5 w-full rounded" /></div>
                      ) : childcare.map((item) => {
                        const lines = item.time_range.split('\n').map(l => l.trim()).filter(l => l);
                        return (
                          <div key={item.id} className="flex justify-between items-baseline py-2.5 border-b border-white/[0.08] last:border-0">
                            <span className="text-white/75 text-[15px] font-medium whitespace-nowrap shrink-0 mr-4">{item.day_label}</span>
                            <div className="flex flex-col items-end gap-0.5">
                              {lines.map((line, idx) => <span key={idx} className="text-white text-[15px] font-semibold tabular-nums whitespace-nowrap">{line}</span>)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab-Button */}
              <button
                type="button"
                onClick={() => setKinderDrawerOpen(!kinderDrawerOpen)}
                className={cn(
                  "flex flex-col items-center justify-center gap-2.5",
                  "w-[3.0rem] py-4 shrink-0",
                  "rounded-xl border cursor-pointer group transition-all duration-300",
                  kinderDrawerOpen ? "border-primary" : "border-white/30"
                )}
                style={{
                  background: "rgba(14,22,32,0.72)",
                  backdropFilter: "blur(32px) saturate(180%)",
                  WebkitBackdropFilter: "blur(32px) saturate(180%)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.45)",
                }}
              >
                <Clock className={cn("w-[1.3rem] h-[1.3rem] shrink-0 transition-all duration-300", kinderDrawerOpen ? "text-primary scale-110" : "text-white/90 group-hover:text-primary group-hover:scale-110")} />
                <span
                  className={cn("text-[14px] font-bold uppercase tracking-[0.18em] font-kanit transition-colors duration-300", kinderDrawerOpen ? "text-primary" : "text-white/90 group-hover:text-primary")}
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                >
                  Kinderbetreuung &amp; Öffnungszeiten
                </span>
                <Baby className={cn("w-[1.3rem] h-[1.3rem] shrink-0 transition-all duration-300", kinderDrawerOpen ? "text-primary scale-110" : "text-white/90 group-hover:text-primary group-hover:scale-110")} />
              </button>
            </div>
          </>
        )}

      </div>

      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-[99] transition-opacity duration-300",
          kinderDrawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setKinderDrawerOpen(false)}
      />

      {/* ── Instagram Social Bar ── */}
      {!preview && <InstagramSocialBar />}

    </section>
  );
}